import React, { useState } from "react";
import MainCalculatorsList from "./MainCalculators";
import SharedHelmet from ".././Helmet";
import RelatedLinks from "../RelatedLinks";
import { FirstAds, SecondAds, MobileUnderCalAds } from "../../Ads";
// Lazy load shared chart components
const SharedPieChart = React.lazy(() => import("../../Sharedpiechart"));
const SharedBarChart = React.lazy(() => import("../../Sharedbarchart"));
const TITLE = "Calculatrice du pourcentage d'absence - Calcul du pourcentage d'absence en fonction de la présence";
const DESCRIPTION =
  "Calculatrice du pourcentage d'absence pour calculer le taux d'absence en fonction du nombre de présences et d'absences. Obtenez un résultat immédiat avec un graphique visuel.";
const KEYWORDS =
  "pourcentage d'absence, calculatrice d'absence, calcul taux absence, présence absence, calculatrice en ligne";
const OG_URL = "https://do-calculate.com/calculator/fr/absence-percentage";
function Calculator() {
  // Main Calculator State
  const [calculationMode, setCalculationMode] = useState("attendance-absence");
  const [totalAttendees, setTotalAttendees] = useState("");
  const [totalAbsences, setTotalAbsences] = useState("");
  const [totalCount, setTotalCount] = useState("");
  const [attendanceOnly, setAttendanceOnly] = useState("");
  const [absencePercentage, setAbsencePercentage] = useState("");
  const [attendancePercentage, setAttendancePercentage] = useState("");
  const [difference, setDifference] = useState("");
  const [error, setError] = useState("");
  const [showChart, setShowChart] = useState(false);
  // Period Calculator State
  const [period, setPeriod] = useState("weekly");
  const [weeklyData, setWeeklyData] = useState([
    { id: 1, attendance: "", absence: "" },
    { id: 2, attendance: "", absence: "" },
  ]);
  const [monthlyData, setMonthlyData] = useState([
    { id: 1, attendance: "", absence: "" },
    { id: 2, attendance: "", absence: "" },
  ]);
  const [dailyData, setDailyData] = useState([
    { id: 1, attendance: "", absence: "" },
    { id: 2, attendance: "", absence: "" },
  ]);
  const [yearlyData, setYearlyData] = useState([
    { id: 1, attendance: "", absence: "" },
    { id: 2, attendance: "", absence: "" },
  ]);
  const [periodResult, setPeriodResult] = useState(null);
  const [periodError, setPeriodError] = useState("");
  const [showPeriodChart, setShowPeriodChart] = useState(false);

  const handleCalculate = () => {
    if (calculationMode === "attendance-absence") {
      if (!totalAttendees || !totalAbsences) {
        setError("Veuillez entrer le nombre de présences et d'absences correctement");
        setAbsencePercentage("");
        setShowChart(false);
        return;
      }
      const attendeesValue = parseInt(totalAttendees);
      const absencesValue = parseInt(totalAbsences);
      if (isNaN(attendeesValue) || isNaN(absencesValue) || attendeesValue < 0 || absencesValue < 0) {
        setError("Veuillez entrer des valeurs correctes et non négatives pour la présence et l'absence");
        setAbsencePercentage("");
        setShowChart(false);
        return;
      }
      const totalValue = attendeesValue + absencesValue;
      const absPercentage = (absencesValue / totalValue) * 100;
      const attPercentage = (attendeesValue / totalValue) * 100;
      const diff = attPercentage - absPercentage;
      setAbsencePercentage(absPercentage.toFixed(2));
      setAttendancePercentage(attPercentage.toFixed(2));
      setDifference(diff.toFixed(2));
      setError("");
      setShowChart(true);
    } else {
      if (!totalCount || !attendanceOnly) {
        setError("Veuillez entrer le nombre total et le nombre de présences correctement");
        setAbsencePercentage("");
        setShowChart(false);
        return;
      }
      const total = parseInt(totalCount);
      const attendance = parseInt(attendanceOnly);
      if (isNaN(total) || isNaN(attendance) || total < 0 || attendance < 0 || attendance > total) {
        setError("Veuillez entrer des valeurs correctes (le nombre de présences doit être inférieur ou égal au nombre total)");
        setAbsencePercentage("");
        setShowChart(false);
        return;
      }
      const absenceValue = total - attendance;
      const absPercentage = (absenceValue / total) * 100;
      const attPercentage = (attendance / total) * 100;
      const diff = attPercentage - absPercentage;
      setAbsencePercentage(absPercentage.toFixed(2));
      setAttendancePercentage(attPercentage.toFixed(2));
      setDifference(diff.toFixed(2));
      setError("");
      setShowChart(true);
    }
  };

  const handlePeriodCalculate = () => {
    let results = [];
    let totalAtt = 0;
    let totalAbs = 0;
    const dataMap = { weekly: weeklyData, monthly: monthlyData, daily: dailyData, yearly: yearlyData };
    const nameMap = { weekly: "Semaine", monthly: "Mois", daily: "Jour", yearly: "Année" };
    const data = dataMap[period];
    const periodName = nameMap[period];
    for (let i = 0; i < data.length; i++) {
      const att = parseInt(data[i].attendance) || 0;
      const abs = parseInt(data[i].absence) || 0;
      if (att < 0 || abs < 0) {
        setPeriodError("Veuillez entrer des valeurs correctes et non négatives");
        setPeriodResult(null);
        setShowPeriodChart(false);
        return;
      }
      const total = att + abs;
      const absPercent = total > 0 ? ((abs / total) * 100).toFixed(2) : "0.00";
      const attPercent = total > 0 ? ((att / total) * 100).toFixed(2) : "0.00";
      results.push({
        name: `${periodName} ${i + 1}`,
        attendance: att,
        absence: abs,
        total: total,
        absencePercentage: absPercent,
        attendancePercentage: attPercent,
      });
      totalAtt += att;
      totalAbs += abs;
    }
    const overallTotal = totalAtt + totalAbs;
    const overallAbsPercent = overallTotal > 0 ? ((totalAbs / overallTotal) * 100).toFixed(2) : "0.00";
    const overallAttPercent = overallTotal > 0 ? ((totalAtt / overallTotal) * 100).toFixed(2) : "0.00";
    setPeriodResult({
      periods: results,
      overall: {
        totalAttendance: totalAtt,
        totalAbsence: totalAbs,
        total: overallTotal,
        absencePercentage: overallAbsPercent,
        attendancePercentage: overallAttPercent,
      },
    });
    setPeriodError("");
    setShowPeriodChart(true);
  };

  const updateWeeklyData = (index, field, value) => { const d = [...weeklyData]; d[index][field] = value; setWeeklyData(d); };
  const updateMonthlyData = (index, field, value) => { const d = [...monthlyData]; d[index][field] = value; setMonthlyData(d); };
  const updateDailyData = (index, field, value) => { const d = [...dailyData]; d[index][field] = value; setDailyData(d); };
  const updateYearlyData = (index, field, value) => { const d = [...yearlyData]; d[index][field] = value; setYearlyData(d); };
  const addWeek = () => setWeeklyData([...weeklyData, { id: weeklyData.length + 1, attendance: "", absence: "" }]);
  const removeWeek = (index) => { if (weeklyData.length > 1) { setWeeklyData(weeklyData.filter((_, i) => i !== index)); setPeriodResult(null); setShowPeriodChart(false); } };
  const addMonth = () => setMonthlyData([...monthlyData, { id: monthlyData.length + 1, attendance: "", absence: "" }]);
  const removeMonth = (index) => { if (monthlyData.length > 1) { setMonthlyData(monthlyData.filter((_, i) => i !== index)); setPeriodResult(null); setShowPeriodChart(false); } };
  const addDay = () => setDailyData([...dailyData, { id: dailyData.length + 1, attendance: "", absence: "" }]);
  const removeDay = (index) => { if (dailyData.length > 1) { setDailyData(dailyData.filter((_, i) => i !== index)); setPeriodResult(null); setShowPeriodChart(false); } };
  const addYear = () => setYearlyData([...yearlyData, { id: yearlyData.length + 1, attendance: "", absence: "" }]);
  const removeYear = (index) => { if (yearlyData.length > 1) { setYearlyData(yearlyData.filter((_, i) => i !== index)); setPeriodResult(null); setShowPeriodChart(false); } };

  return (
    <div className="ltr">
      <SharedHelmet
        TITLE={TITLE}
        DESCRIPTION={DESCRIPTION}
        KEYWORDS={KEYWORDS}
        OG_URL={OG_URL}
      />
      <div className="row">
        <div className="col-12 col-md-8">
          <FirstAds />
        </div>
      </div>
      <div className="row">
        <div className="col-12 col-md-8">
          <br />
          <div className="bg-success text-white text-center" style={{ height: "50px" }}>
            <h1 style={{ fontSize: "17px" }}>{TITLE}</h1>
          </div>
          <div className="border border-light">
            <div className="row">
              <div>
                <h2 style={{ fontSize: "18px" }}>Comment calculer le pourcentage d'absence</h2>
                <p>
                  Pour calculer le pourcentage d'absence, vous devez calculer le nombre total d'absences et connaître le nombre total de personnes présentes et absentes, puis nous divisons le nombre d'absences par le nombre total et après cela nous multiplions par 100% pour obtenir le pourcentage d'absence.
                </p>
                <p>
                  Pourcentage d'absence = (Nombre d'absences / (Nombre de présences + Nombre d'absences)) * 100%
                </p>
                <p>
                  Cette calculatrice peut être utilisée pour calculer le pourcentage d'absence en fonction des chiffres saisis. La calculatrice offre deux options d'entrée : soit en entrant le nombre de présences et d'absences directement, soit en entrant le nombre total et le nombre de présences uniquement. La calculatrice affiche également un diagramme en camembert qui montre le pourcentage de présence et d'absence de manière visuelle, ainsi que le calcul de la différence entre les deux pourcentages.
                </p>
                <p>
                  Vous pouvez aussi utiliser la calculatrice de pourcentage d'absence hebdomadaire et mensuelle pour suivre les taux de présence et d'absence sur plusieurs semaines ou mois, avec la possibilité d'ajouter ou supprimer des périodes. Les résultats s'affichent dans un tableau détaillé avec un graphique en barres.
                </p>
                <hr />
              </div>
            </div>
            <MobileUnderCalAds />
            {/* Main Calculator */}
            <div className="row">
              <div className="col-sm-12 col-md-7 calculatorStyle">
                <div className="p-3 mb-2 bg-success text-white">
                  <h2 style={{ fontSize: "16px" }}>{TITLE}</h2>
                </div>
                <hr />
                <div>
                  <div className="mb-3">
                    <label><b>Mode de calcul :</b></label>
                    <br />
                    <div className="form-check">
                      <input
                        className="form-check-input"
                        type="radio"
                        name="calculationMode"
                        id="mode1"
                        value="attendance-absence"
                        checked={calculationMode === "attendance-absence"}
                        onChange={(e) => { setCalculationMode(e.target.value); setAbsencePercentage(""); setError(""); setShowChart(false); }}
                      />
                      <label className="form-check-label" htmlFor="mode1">
                        Entrer le nombre de présences et d'absences
                      </label>
                    </div>
                    <div className="form-check">
                      <input
                        className="form-check-input"
                        type="radio"
                        name="calculationMode"
                        id="mode2"
                        value="total-attendance"
                        checked={calculationMode === "total-attendance"}
                        onChange={(e) => { setCalculationMode(e.target.value); setAbsencePercentage(""); setError(""); setShowChart(false); }}
                      />
                      <label className="form-check-label" htmlFor="mode2">
                        Entrer le nombre total et le nombre de présences uniquement
                      </label>
                    </div>
                  </div>
                  <div>
                    {calculationMode === "attendance-absence" ? (
                      <>
                        <div className="form-group d-flex align-items-center">
                          <b><label className="form-label ms-2">Nombre de présences</label></b>
                          <input type="number" placeholder="Nombre de présences" value={totalAttendees} onChange={(e) => setTotalAttendees(e.target.value)} className="form-control" style={{ width: "90%" }} />
                        </div>
                        <br />
                        <div className="form-group d-flex align-items-center">
                          <b><label className="form-label ms-2">Nombre d'absences</label></b>
                          <input type="number" placeholder="Nombre d'absences" value={totalAbsences} onChange={(e) => setTotalAbsences(e.target.value)} className="form-control" style={{ width: "90%" }} />
                        </div>
                      </>
                    ) : (
                      <>
                        <div className="form-group d-flex align-items-center">
                          <b><label className="form-label ms-2">Nombre total</label></b>
                          <input type="number" placeholder="Nombre total" value={totalCount} onChange={(e) => setTotalCount(e.target.value)} className="form-control" style={{ width: "90%" }} />
                        </div>
                        <br />
                        <div className="form-group d-flex align-items-center">
                          <b><label className="form-label ms-2">Nombre de présences</label></b>
                          <input type="number" placeholder="Nombre de présences" value={attendanceOnly} onChange={(e) => setAttendanceOnly(e.target.value)} className="form-control" style={{ width: "90%" }} />
                        </div>
                      </>
                    )}
                    <br />
                    <button className="btn btn-success" style={{ fontSize: "20px" }} onClick={handleCalculate}>
                      Calculer le pourcentage d'absence
                    </button>
                  </div>
                  <br />
                  <div>
                    {error !== "" ? (
                      <p className="p-3 mb-2 bg-warning text-white">Erreur : {error}</p>
                    ) : (
                      <>
                        {absencePercentage !== "" && (
                          <>
                            <p className="p-3 mb-2 bg-success text-white">Pourcentage d'absence : {absencePercentage}%</p>
                            <p className="p-3 mb-2 bg-success text-white">Pourcentage de présence : {attendancePercentage}%</p>
                            <p className="p-3 mb-2 bg-success text-white">
                              Différence : {Math.abs(parseFloat(difference))}%{" "}
                              {parseFloat(difference) > 0 ? "(présence plus élevée)" : parseFloat(difference) < 0 ? "(absence plus élevée)" : "(égale)"}
                            </p>
                          </>
                        )}
                      </>
                    )}
                  </div>
                </div>
              </div>
              <div className="col-sm-12 col-md-5">
                {showChart && absencePercentage && (
                  <React.Suspense fallback={<div className="text-center p-3">Chargement du graphique...</div>}>
                    <SharedPieChart
                      firstLabel="Pourcentage de présence"
                      secondLabel="Pourcentage d'absence"
                      firstValue={attendancePercentage}
                      secondValue={absencePercentage}
                      firstColor="#28a745"
                      secondColor="#dc3545"
                      title="Graphique"
                    />
                  </React.Suspense>
                )}
              </div>
            </div>
            <hr />
            {/* Period Calculator */}
            <div className="row">
              <div className="col-sm-12 col-md-5 calculatorStyle">
                <div className="p-3 mb-2 bg-success text-white">
                  <h2 style={{ fontSize: "16px" }}>Calculatrice d'absence hebdomadaire et mensuelle</h2>
                </div>
                <hr />
                <div>
                  <div className="mb-3">
                    <label className="ms-2"><b>Choisir la période :</b></label>
                    <select
                      className="form-select"
                      style={{ width: "200px", display: "inline-block" }}
                      value={period}
                      onChange={(e) => { setPeriod(e.target.value); setPeriodResult(null); setShowPeriodChart(false); }}
                    >
                      <option value="daily">Quotidien</option>
                      <option value="weekly">Hebdomadaire</option>
                      <option value="monthly">Mensuel</option>
                      <option value="yearly">Annuel</option>
                    </select>
                  </div>
                  {(period === "daily" ? dailyData : period === "yearly" ? yearlyData : period === "weekly" ? weeklyData : monthlyData).map((item, index) => {
                    const label = { daily: "Jour", weekly: "Semaine", monthly: "Mois", yearly: "Année" }[period];
                    const updateFn = { daily: updateDailyData, weekly: updateWeeklyData, monthly: updateMonthlyData, yearly: updateYearlyData }[period];
                    const removeFn = { daily: removeDay, weekly: removeWeek, monthly: removeMonth, yearly: removeYear }[period];
                    const currentData = { daily: dailyData, weekly: weeklyData, monthly: monthlyData, yearly: yearlyData }[period];
                    return (
                      <div key={item.id} className="mb-3">
                        <div className="d-flex justify-content-between align-items-center">
                          <h3 style={{ fontSize: "15px" }}><b>{label} {index + 1}</b></h3>
                          {currentData.length > 1 && (
                            <button className="btn btn-sm btn-secondary" onClick={() => removeFn(index)}>Supprimer</button>
                          )}
                        </div>
                        <div className="d-flex gap-2 align-items-center mb-2">
                          <label style={{ minWidth: "90px" }}>Présences :</label>
                          <input type="number" className="form-control" style={{ width: "120px" }} value={item.attendance} onChange={(e) => updateFn(index, "attendance", e.target.value)} />
                        </div>
                        <div className="d-flex gap-2 align-items-center">
                          <label style={{ minWidth: "90px" }}>Absences :</label>
                          <input type="number" className="form-control" style={{ width: "120px" }} value={item.absence} onChange={(e) => updateFn(index, "absence", e.target.value)} />
                        </div>
                      </div>
                    );
                  })}
                  <button className="btn btn-sm btn-secondary mb-3" onClick={{ daily: addDay, weekly: addWeek, monthly: addMonth, yearly: addYear }[period]}>
                    + Ajouter {{ daily: "un jour", weekly: "une semaine", monthly: "un mois", yearly: "une année" }[period]}
                  </button>
                  <br />
                  <button className="btn btn-success" style={{ fontSize: "20px" }} onClick={handlePeriodCalculate}>
                    Calculer les pourcentages
                  </button>
                  <br />
                  <div>
                    {periodError !== "" ? (
                      <p className="p-3 mb-2 bg-warning text-white">Erreur : {periodError}</p>
                    ) : null}
                  </div>
                </div>
              </div>
            </div>
            {periodResult && (
              <div className="col-sm-12 col-md-7 calculatorStyle">
                <h2 className="p-3 mb-2 bg-success text-white" style={{ fontSize: "16px" }}>Résultats</h2>
                <div style={{ overflowX: "auto", width: "100%" }}>
                  <table style={{ fontSize: "12px", width: "100%" }}>
                    <thead>
                      <tr>
                        <th>Période</th>
                        <th>Présences</th>
                        <th>Absences</th>
                        <th>Total</th>
                        <th>% Absence</th>
                        <th>% Présence</th>
                      </tr>
                    </thead>
                    <tbody>
                      {periodResult.periods.map((p, index) => (
                        <tr key={index}>
                          <td>{p.name}</td>
                          <td>{p.attendance}</td>
                          <td>{p.absence}</td>
                          <td>{p.total}</td>
                          <td>{p.absencePercentage}%</td>
                          <td>{p.attendancePercentage}%</td>
                        </tr>
                      ))}
                      <tr style={{ backgroundColor: "#f8f9fa", fontWeight: "bold" }}>
                        <td>Total</td>
                        <td>{periodResult.overall.totalAttendance}</td>
                        <td>{periodResult.overall.totalAbsence}</td>
                        <td>{periodResult.overall.total}</td>
                        <td>{periodResult.overall.absencePercentage}%</td>
                        <td>{periodResult.overall.attendancePercentage}%</td>
                      </tr>
                    </tbody>
                  </table>
                  {showPeriodChart && (
                    <React.Suspense fallback={<div className="text-center p-3">Chargement du graphique...</div>}>
                      <SharedBarChart
                        periodResult={periodResult}
                        firstLabel="% Présence"
                        secondLabel="% Absence"
                        firstColor="rgba(40, 167, 69, 0.8)"
                        secondColor="rgba(220, 53, 69, 0.8)"
                        firstBorderColor="rgba(40, 167, 69, 1)"
                        secondBorderColor="rgba(220, 53, 69, 1)"
                        title="Graphique des pourcentages"
                      />
                    </React.Suspense>
                  )}
                </div>
              </div>
            )}
            <hr />
            <div className="row">
              <RelatedLinks />
              <hr />
            </div>
          </div>
        </div>
        <div className="col">
          <SecondAds />
          <br />
          <MainCalculatorsList></MainCalculatorsList>
          <br />
        </div>
      </div>

    </div>
  );
}
export default Calculator;
